import axios from "axios";
import store from ".././store";
import authHeader from "./auth-header";
import axiosInstance from "./api";
import BASE_API_URL from "@/globals/api";

// Derived from globals/api.js rather than duplicating the hostname. The old
// copy used 127.0.0.1 locally while everything else used localhost — two
// different origins, so a browser treated them as separate for CORS purposes.
const API_URL = `${BASE_API_URL}/auth/`;

/* The backend's password minimum, for the helper text under every new-password
   field. It must match BOTH `.min('password', 6)` in register
   (backend-nextja/src/server/auth.ts) and MINIMUM_PASSWORD_LENGTH in
   src/server/passwordReset.ts — the server is what enforces it; this only
   says it out loud. */
export const PASSWORD_MIN_LENGTH = 6;

class AuthService {
  resendVerify() {
    return axiosInstance.post(API_URL + "email/verification-notification", {});
  }

  login(user) {
    return axios
      .post(API_URL + "login", {
        email: user.email,
        password: user.password,
        two_factor_code: user.two_factor_code,
        token: user.token
      })
      .then((response) => {
        if (response.data.access_token) {
          localStorage.setItem("user", JSON.stringify(response.data));
        }
        return response.data;
      });
  }

  /**
   * Set scrcl
   * @param {*} scrcl 
   * @returns 
   */
  setScrcl(scrcl) {
    return axiosInstance.post(
      `${API_URL}scrcl`,
      {
        scrcl
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeader(),
        },
      }
    );
  }

  /**
 * Reset API Key
 * @returns 
 */
  resetApiKey() {
    return axiosInstance.post(
      `${API_URL}reset-api-key`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeader(),
        },
      }
    );
  }

  /**
   * Set IP
   * @param {*} ip 
   * @returns 
   */
  setIp(ip) {
    return axiosInstance.post(
      `${API_URL}ip`,
      {
        ip
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeader(),
        },
      }
    );
  }

  /**
   * Fingerprint
   * @returns 
   */
  setFingerprint(fingerprint) {
    return axiosInstance.post(
      `${API_URL}fingerprint`,
      {
        fingerprint
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeader(),
        },
      }
    );
  }

  getUser() {
    store.dispatch("auth/toggleLoading");
    return axios
      .get(`${API_URL}user-profile`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeader(),
        },
      })
      .then((response) => {
        const user = JSON.parse(localStorage.getItem("user"));
        const token = user.access_token;
        const expires = user.expires_in;
        const token_type = user.token_type;

        const compare = {
          access_token: token,
          expires_in: expires,
          token_type: token_type,
          user: response.data,
        };
        localStorage.setItem("user", JSON.stringify(compare));
        store.dispatch("auth/updateUser", compare.user);
      })
      .catch((err) => {
        store.dispatch("auth/toggleLoading");
      });
  }

  logout() {
    localStorage.removeItem("user");

    // The cart is per-account, and this browser may well be shared. Leaving it
    // behind meant the next person to sign in inherited the previous
    // customer's cart — and, now that carts sync, their client would push it
    // up as that new account's own.
    //
    // Local only: the account's saved cart is deliberately untouched, because
    // the customer still wants it next time they sign in.
    localStorage.removeItem("cart");
    localStorage.removeItem("cart_updated_at");

    // Same reason: the campaign list is cached for a fast first paint, and the
    // next account to sign in on this browser was shown the previous one's
    // campaigns until its own list arrived.
    localStorage.removeItem("jobs");
  }

  //JSON.stringify(localStorage.getItem("user"))
  refresh() {
    return axios
      .post(
        API_URL + "refresh",
        {},
        {
          headers: {
            "Content-Type": "application/json",
            ...authHeader(),
          },
        }
      )
      .then(async (response) => {
        //First get new token
        const json = JSON.parse(localStorage.getItem("user"));
        const compare = { ...json, ...response.data };
        localStorage.setItem("user", JSON.stringify(compare));

        //Then update user
        await this.getUser();
        return response.data;
      });
  }

  isTokenExpired() {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const token = JSON.parse(user).access_token;
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map(function (c) {
              return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join("")
        );
        const { exp } = JSON.parse(jsonPayload);
        const expired = Date.now() >= exp * 1000;
        // if (expired) {
        //   this.refresh();
        //   // location.reload(true);
        // }
        return expired;
      } catch (e) {
        // Stored token is not a decodable JWT — treat it as expired and drop it
        localStorage.removeItem("user");
        return true;
      }
    }
  }

  register(user) {
    return axios.post(API_URL + "register", {
      name: user.username,
      email: user.email,
      password: user.password,
      password_confirmation: user.password_confirmation,
      token: user.token
    });
  }
}

export default new AuthService();
